package uz.mint.frontend.api

import uz.mint.frontend.SiteConfig

/**
 * The customer's own account: profile, purchased eSIMs, order history and active sessions.
 *
 * Nothing here takes a customer identifier. Scope comes from the session cookie on the server,
 * which is the only place it can be enforced — the provider's account holds every profile we
 * have ever sold and would happily return any of them to a request that named one.
 */
object AccountApi {

    suspend fun profile(): Customer? {
        val response = ApiClient.get("/api/me")
        if (!response.ok) return null
        return response.json<CustomerJson>().toCustomer()
    }

    /**
     * The profile is a name and a language, and nothing else the customer can type.
     *
     * There is deliberately no e-mail here: the site never sends mail — the QR, the activation
     * link and the order history all live on the account pages — so a field asking for an address
     * only promises a channel that does not exist. The backend still accepts and stores one for
     * the addresses collected while the field was on the page; an omitted key leaves it alone.
     */
    suspend fun updateProfile(name: String?, locale: String?): Customer? {
        val fields = buildList {
            name?.let { add("\"name\":${it.jsonString()}") }
            locale?.let { add("\"locale\":${it.jsonString()}") }
        }
        val response = ApiClient.patch("/api/me", "{${fields.joinToString(",")}}")
        if (!response.ok) throw ApiFailure(response.status)
        return response.json<CustomerJson>().toCustomer()
    }

    suspend fun esims(): List<CustomerEsim> {
        val response = ApiClient.get("/api/me/esims")
        if (!response.ok) throw ApiFailure(response.status)
        return response.json<Array<EsimJson>>().map { it.toEsim() }
    }

    /**
     * Where the eSIM's QR lives — an address for `<img src>`, not something to fetch.
     *
     * The backend draws it from the activation code, so it exists exactly when the code does.
     * It needs the session cookie like every other call here, and gets it without
     * `credentials: include`: an image request carries cookies for its own site, and in
     * development :8080 and :8081 differ only by port, which does not make a different site. In
     * production the storefront and the API share one origin, so the question does not arise.
     */
    fun esimQrUrl(iccid: String): String =
        "${SiteConfig.apiOrigin}/api/me/esims/${encodeURIComponent(iccid)}/qr"

    /** Pulls fresh status and usage for one profile. The server rate-limits this per eSIM. */
    suspend fun refreshEsim(iccid: String): CustomerEsim {
        val response = ApiClient.post("/api/me/esims/${encodeURIComponent(iccid)}/refresh")
        if (!response.ok) throw ApiFailure(response.status)
        return response.json<EsimJson>().toEsim()
    }

    /**
     * Starts a purchase and returns one cashier link per configured payment provider.
     *
     * The order is created server-side with the price frozen at this moment, because that is
     * the figure the cashier will charge and the figure its callback will be verified against.
     */
    suspend fun createOrder(packageCode: String, lang: String): CreatedOrder {
        val response = ApiClient.post(
            "/api/me/orders",
            """{"packageCode":${packageCode.jsonString()},"lang":${lang.jsonString()}}""",
        )
        if (!response.ok) throw ApiFailure(response.status)
        val raw = response.json<CreatedOrderJson>()

        // Falls back to the single `payUrl` when the backend predates paymentOptions: one static
        // export is served against whatever API happens to be deployed, and a checkout that
        // silently loses its only button is worse than one that offers an unnamed cashier.
        val options = raw.paymentOptions?.map { PaymentOption(it.id ?: "", it.name ?: "", it.payUrl ?: "") }
            ?.filter { it.payUrl.isNotEmpty() }
            ?: listOfNotNull(raw.payUrl?.let { PaymentOption("click", "Click", it) })

        return CreatedOrder(
            reference = raw.order?.reference ?: "",
            options = options,
            payable = options.isNotEmpty(),
        )
    }

    suspend fun orders(): List<CustomerOrder> {
        val response = ApiClient.get("/api/me/orders")
        if (!response.ok) throw ApiFailure(response.status)
        return response.json<Array<OrderJson>>().map { it.toOrder() }
    }

    suspend fun sessions(): List<AccountSession> {
        val response = ApiClient.get("/api/me/sessions")
        if (!response.ok) throw ApiFailure(response.status)
        return response.json<Array<SessionJson>>().map { it.toSession() }
    }

    /** Ends every other browser but this one. */
    suspend fun revokeOtherSessions() {
        val response = ApiClient.delete("/api/me/sessions")
        if (!response.ok) throw ApiFailure(response.status)
    }
}

class ApiFailure(val status: Int) : RuntimeException("Request failed with HTTP $status")

/**
 * @param payable false when no cashier is configured. The order still exists — the customer is
 *   pointed at Telegram rather than at a button that goes nowhere.
 */
data class CreatedOrder(
    val reference: String,
    val options: List<PaymentOption>,
    val payable: Boolean,
)

/** One cashier the customer may be sent to. [name] is a brand, so it is not translated. */
data class PaymentOption(
    val id: String,
    val name: String,
    val payUrl: String,
)

private external interface CreatedOrderJson {
    val order: OrderJson?
    val paymentOptions: Array<PaymentOptionJson>?
    val payUrl: String?
    val payable: Boolean?
}

private external interface PaymentOptionJson {
    val id: String?
    val name: String?
    val payUrl: String?
}

data class CustomerEsim(
    val iccid: String,
    val planName: String?,
    /** The LPA string behind the QR code. Null until the provider has allocated the profile. */
    val activationCode: String?,
    val qrCodeUrl: String?,
    /**
     * The provider's own installation page: the QR plus the SM-DP+ details for typing in by hand.
     * Preferred over showing the raw LPA string, which is a hundred characters of noise to anyone
     * who is not already an engineer.
     */
    val shortUrl: String?,
    val esimStatus: String?,
    val smdpStatus: String?,
    val dataTotalMb: Int?,
    val dataUsedMb: Int?,
    val dataRemainingMb: Int?,
    val expiresAt: String?,
    val daysLeft: Int?,
    val orderReference: String?,
) {
    /** 0..1 for the usage bar; null when the plan has no stated allowance. */
    val usedFraction: Double?
        get() {
            val total = dataTotalMb?.takeIf { it > 0 } ?: return null
            return ((dataUsedMb ?: 0).toDouble() / total).coerceIn(0.0, 1.0)
        }
}

data class CustomerOrder(
    val reference: String,
    val planName: String,
    val priceUzs: Double?,
    val status: String,
    val createdAt: String,
    /** Which cashier confirmed it; null while the order is only created. */
    val paymentMethod: String?,
)

data class AccountSession(
    val id: Int,
    val current: Boolean,
    val lastSeenAt: String,
    val userAgent: String?,
    val ipAddress: String?,
)

private external interface EsimJson {
    val iccid: String?
    val planName: String?
    val activationCode: String?
    val qrCodeUrl: String?
    val shortUrl: String?
    val esimStatus: String?
    val smdpStatus: String?
    val dataTotalMb: Int?
    val dataUsedMb: Int?
    val dataRemainingMb: Int?
    val expiresAt: String?
    val daysLeft: Int?
    val orderReference: String?
}

private fun EsimJson.toEsim() = CustomerEsim(
    iccid = iccid ?: throw CatalogException("eSIM entry is missing 'iccid'"),
    planName = planName,
    activationCode = activationCode,
    qrCodeUrl = qrCodeUrl,
    shortUrl = shortUrl,
    esimStatus = esimStatus,
    smdpStatus = smdpStatus,
    dataTotalMb = dataTotalMb,
    dataUsedMb = dataUsedMb,
    dataRemainingMb = dataRemainingMb,
    expiresAt = expiresAt,
    daysLeft = daysLeft,
    orderReference = orderReference,
)

private external interface OrderJson {
    val reference: String?
    val planName: String?
    val priceUzs: Double?
    val status: String?
    val createdAt: String?
    val paymentMethod: String?
}

private fun OrderJson.toOrder() = CustomerOrder(
    reference = reference ?: "—",
    planName = planName ?: "—",
    priceUzs = priceUzs,
    status = status ?: "CREATED",
    createdAt = createdAt ?: "",
    paymentMethod = paymentMethod,
)

private external interface SessionJson {
    val id: Int?
    val current: Boolean?
    val lastSeenAt: String?
    val userAgent: String?
    val ipAddress: String?
}

private fun SessionJson.toSession() = AccountSession(
    id = id ?: 0,
    current = current == true,
    lastSeenAt = lastSeenAt ?: "",
    userAgent = userAgent,
    ipAddress = ipAddress,
)
